import { AlgoliaHit } from 'instantsearch.js';
import { ProductHit } from '../../search/search';
import { ProductFragmentFragment } from '../../..//src/queries/__generated__/productFragment.generated';
import { extractSocialTagsFromDiscoveryHit, SocialTag } from '../../common/utils/social-tags.utils';
import { Base_Unit, Packaging, Product_Sub_Family_Name_Enum } from '../../../../../packages/graphql/packages/graphql/types';
import { FormatBaseUnitQuantity, FormatLogisticalPackaging, FormatPackagingVolumePrice } from '../../../../../packages/utils/packages/formatter/packaging-formatter';
import { ProductCardData } from './product-card.types';

export function mapAlgoliaHitToProductCardData(hit: AlgoliaHit<ProductHit>): ProductCardData {
  return {
    id: hit.packaging_id,
    packagingId: hit.packaging_id,
    companyId: hit.company_id,
    productSku: Number(hit.productSku),
    baseUnitSku: Number(hit.base_unit_sku),
    packagingSku: parseInt(hit.objectID.split('-')[0], 10),
    name: hit.name,
    photo: hit.photo,
    family: hit.family,
    baseUnitLabel: hit.baseUnit,
    baseUnitType: hit.baseUnitType,
    unitQuantity: hit.unitQuantity,
    packagingLabel: hit.packaging,
    labels: hit.labels,
    price: hit.price,
    billingType: hit.billingType,
    volumePrice: hit.volumePrice,
    suggestedRetailPrice: hit.suggestedRetailPrice,
    baseUnitNetContent: hit.baseUnitNetContent,
    packagingNetContent: hit.packagingNetContent,
    baseUnitMeasurementUnit: hit.baseUnitMeasurementUnit,
    packagingMeasurementUnit: hit.packagingMeasurementUnit,
    packagingTradeItemUnitDescriptor: hit.packagingTradeItemUnitDescriptor,
    company: hit.company,
    companyPhoto: hit.companyPhoto,
    socialTags: extractSocialTagsFromDiscoveryHit(hit),
  };
}

function mapSocialTagsFromBaseUnit(socialTags: ProductFragmentFragment['base_units'][number]['social_tags']): SocialTag[] {
  return socialTags.map(({ type, level, priority }) => ({
    type,
    level,
    priority,
  }));
}

export function mapProductFragmentToProductCardData(product: ProductFragmentFragment): ProductCardData {
  const numberFormatter = new Intl.NumberFormat('fr', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  const baseUnit = product.base_units[0];
  const packaging = baseUnit.packagings[0];

  return {
    id: packaging.id,
    packagingId: packaging.id,
    companyId: product.company_id,
    productSku: product.sku,
    baseUnitSku: baseUnit.sku!,
    packagingSku: packaging.sku,
    name: product.name,
    photo: baseUnit.main_photo || null,
    family: product.sub_family as Product_Sub_Family_Name_Enum,
    baseUnitLabel: FormatBaseUnitQuantity(baseUnit as Base_Unit, 'fr', numberFormatter),
    baseUnitType: baseUnit.unit_type!,
    unitQuantity: packaging.unit_quantity || 1,
    packagingLabel: FormatLogisticalPackaging(packaging as Packaging, 'fr', baseUnit as Base_Unit),
    labels: product.labels.map(({ label }) => label),
    price: packaging.price,
    billingType: baseUnit.billing_type,
    volumePrice: FormatPackagingVolumePrice(packaging as Packaging, baseUnit as Base_Unit),
    suggestedRetailPrice: baseUnit.suggested_retail_price,
    baseUnitNetContent: baseUnit.unit_net_content || null,
    packagingNetContent: packaging.net_content || null,
    baseUnitMeasurementUnit: baseUnit.content_measurement_unit || null,
    packagingMeasurementUnit: packaging.content_measurement_unit || null,
    packagingTradeItemUnitDescriptor: packaging.trade_item_unit_descriptor,
    company: product.company.tradeName,
    companyPhoto: product.company.photos[0]?.photo || null,
    socialTags: mapSocialTagsFromBaseUnit(baseUnit.social_tags),
  };
}
